import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { complianceChecklistApi } from '../lib/api';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface ChecklistItem {
  id: string;
  label: string;
  required: boolean;
  category?: string;
}

interface Checklist {
  id: string;
  name: string;
  description?: string | null;
  items: ChecklistItem[];
  isActive: boolean;
  createdAt: string;
}

interface ChecklistEditorProps {
  teamId: string;
  checklists: Checklist[];
}

function ChecklistItemRow({
  item,
  onUpdate,
  onRemove,
}: {
  item: ChecklistItem;
  onUpdate: (item: ChecklistItem) => void;
  onRemove: () => void;
}) {
  return (
    <div className="flex items-center gap-3 py-2 px-3 bg-gray-50 rounded-md">
      <input
        type="text"
        value={item.label}
        onChange={(e) => onUpdate({ ...item, label: e.target.value })}
        placeholder="Checklist item description"
        className="flex-1 text-sm border border-gray-300 rounded px-2 py-1 focus:border-ocean-500 focus:outline-none"
      />
      <input
        type="text"
        value={item.category || ''}
        onChange={(e) => onUpdate({ ...item, category: e.target.value || undefined })}
        placeholder="Category"
        className="w-32 text-sm border border-gray-300 rounded px-2 py-1 focus:border-ocean-500 focus:outline-none"
      />
      <label className="flex items-center gap-1 text-xs text-gray-600 whitespace-nowrap">
        <input
          type="checkbox"
          checked={item.required}
          onChange={(e) => onUpdate({ ...item, required: e.target.checked })}
          className="rounded border-gray-300 text-ocean-600 focus:ring-ocean-500"
        />
        Required
      </label>
      <button
        onClick={onRemove}
        className="text-gray-400 hover:text-red-500 transition-colors"
      >
        <TrashIcon className="h-4 w-4" />
      </button>
    </div>
  );
}

function ChecklistForm({
  teamId,
  checklist,
  onCancel,
}: {
  teamId: string;
  checklist?: Checklist;
  onCancel: () => void;
}) {
  const queryClient = useQueryClient();
  const [name, setName] = useState(checklist?.name || '');
  const [description, setDescription] = useState(checklist?.description || '');
  const [items, setItems] = useState<ChecklistItem[]>(checklist?.items || []);

  const createMutation = useMutation({
    mutationFn: () =>
      complianceChecklistApi.createChecklist(teamId, { name, description, items }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists', teamId] });
      toast.success('Checklist created');
      onCancel();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to create checklist');
    },
  });

  const updateMutation = useMutation({
    mutationFn: () =>
      complianceChecklistApi.updateChecklist(teamId, checklist!.id, {
        name,
        description: description || null,
        items,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists', teamId] });
      toast.success('Checklist updated');
      onCancel();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.error || 'Failed to update checklist');
    },
  });

  const addItem = () => {
    setItems([...items, { id: crypto.randomUUID(), label: '', required: false }]);
  };

  const updateItem = (index: number, updated: ChecklistItem) => {
    const newItems = [...items];
    newItems[index] = updated;
    setItems(newItems);
  };

  const removeItem = (index: number) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    const validItems = items.filter((i) => i.label.trim());
    if (validItems.length === 0) {
      toast.error('Add at least one checklist item');
      return;
    }
    setItems(validItems);
    if (checklist) {
      updateMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="border border-gray-200 rounded-lg p-4 bg-white space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., FDIC Ad Review Checklist"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-ocean-500 focus:outline-none focus:ring-ocean-500"
          />
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">Checklist Items</label>
          <button
            type="button"
            onClick={addItem}
            className="inline-flex items-center gap-1 text-xs text-ocean-600 hover:text-ocean-700 font-medium"
          >
            <PlusIcon className="h-3.5 w-3.5" />
            Add Item
          </button>
        </div>
        <div className="space-y-2">
          {items.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No items yet. Click "Add Item" to start.</p>
          ) : (
            items.map((item, index) => (
              <ChecklistItemRow
                key={item.id}
                item={item}
                onUpdate={(updated) => updateItem(index, updated)}
                onRemove={() => removeItem(index)}
              />
            ))
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-3 pt-2 border-t border-gray-200">
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 font-medium"
        >
          <XMarkIcon className="h-4 w-4" />
          Cancel
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="inline-flex items-center gap-1 px-4 py-1.5 bg-ocean-600 text-white text-sm font-medium rounded-md hover:bg-ocean-700 disabled:opacity-50 transition-colors"
        >
          <CheckIcon className="h-4 w-4" />
          {isPending ? 'Saving...' : checklist ? 'Update' : 'Create'}
        </button>
      </div>
    </form>
  );
}

export default function ChecklistEditor({ teamId, checklists }: ChecklistEditorProps) {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const deactivateMutation = useMutation({
    mutationFn: (checklistId: string) =>
      complianceChecklistApi.updateChecklist(teamId, checklistId, { isActive: false }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['checklists', teamId] });
      toast.success('Checklist archived');
    },
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-navy-900">Compliance Checklists</h3>
        {!showForm && !editingId && (
          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-ocean-600 text-white text-sm font-medium rounded-md hover:bg-ocean-700 transition-colors"
          >
            <PlusIcon className="h-4 w-4" />
            New Checklist
          </button>
        )}
      </div>

      {showForm && (
        <ChecklistForm teamId={teamId} onCancel={() => setShowForm(false)} />
      )}

      {checklists.length === 0 && !showForm ? (
        <p className="text-sm text-gray-500 text-center py-8">
          No checklists yet. Create one to track compliance review steps for your team.
        </p>
      ) : (
        <div className="space-y-3">
          {checklists.map((checklist) => (
            <div key={checklist.id}>
              {editingId === checklist.id ? (
                <ChecklistForm
                  teamId={teamId}
                  checklist={checklist}
                  onCancel={() => setEditingId(null)}
                />
              ) : (
                <div className="border border-gray-200 rounded-lg p-4 bg-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="text-sm font-semibold text-gray-900">{checklist.name}</h4>
                      {checklist.description && (
                        <p className="text-xs text-gray-500 mt-0.5">{checklist.description}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        {(checklist.items || []).length} items
                        {' '}({(checklist.items || []).filter((i) => i.required).length} required)
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setEditingId(checklist.id)}
                        className="text-gray-400 hover:text-ocean-600 transition-colors"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => deactivateMutation.mutate(checklist.id)}
                        className="text-gray-400 hover:text-red-500 transition-colors"
                        title="Archive"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Items preview */}
                  <div className="mt-3 space-y-1">
                    {(checklist.items || []).slice(0, 5).map((item) => (
                      <div key={item.id} className="flex items-center gap-2 text-xs text-gray-600">
                        <div className="w-3 h-3 rounded border border-gray-300 flex-shrink-0" />
                        <span className={item.required ? 'font-medium' : ''}>{item.label}</span>
                        {item.required && (
                          <span className="text-red-500 text-[10px]">*</span>
                        )}
                        {item.category && (
                          <span className="text-gray-400 text-[10px]">({item.category})</span>
                        )}
                      </div>
                    ))}
                    {(checklist.items || []).length > 5 && (
                      <p className="text-[10px] text-gray-400 ml-5">
                        +{(checklist.items || []).length - 5} more items
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
